/**
 * Pure display rules for the employer chat UI: channel category, the two
 * deletion clocks, the hiring-stage machine, search, coin eligibility, and inbox
 * filter/group. No React, no infrastructure — every function is a pure
 * derivation over the domain shapes. Ported from the mock's
 * `domain/channel/channelRules` + `gamification` + `participant` +
 * `application/inbox/InboxService`.
 */
import { isBroadcastChannel } from '@chat-emp/shared';

import type { RetentionConfig } from './config.js';
import type {
  ApplicationStatus,
  Channel,
  ChannelId,
  ClosingVariant,
  FirstReplyReward,
  GamificationConfig,
  GroupBy,
  InboxCriteria,
  InboxGroup,
  InboxItem,
  Participant,
} from './domain.js';

// ── channel category ─────────────────────────────────────────────────────────
export function needsReply(channel: Channel): boolean {
  return channel.unrepliedCount > 0;
}

/** Ended chats are read-only and leave the inbox — see `channelCategory`. */
export function isEnded(channel: Channel): boolean {
  return channel.phase !== 'open';
}

/** Which rail a channel belongs to. Total and exclusive by construction. */
export type ChannelCategory = 'inbox' | 'ended' | 'broadcast';

export function channelCategory(channel: Channel): ChannelCategory {
  if (isBroadcast(channel)) return 'broadcast';
  return isEnded(channel) ? 'ended' : 'inbox';
}

// ── deletion clocks ──────────────────────────────────────────────────────────
export interface Deletion {
  reason: 'read' | 'inactivity';
  /** When the window opened. Only useful for showing elapsed progress. */
  startedAt: number;
  /** When the chat is hard-deleted. */
  at: number;
}

/**
 * When a chat is deleted, and by which clock, in order of authority:
 *
 *   1. `deleteAt` — the end-chat read window, stamped by the backend.
 *   2. `inactivityDeleteAt` — the idle deadline the sweep intends to act on,
 *      also stamped by the backend. Preferred whenever present, because it is
 *      what will actually happen rather than what we predict.
 *   3. The local window from the last message. A fallback for channels the
 *      backend has not stamped, which is most of them: it only stamps chats
 *      close to deletion.
 *
 * Never null while the inactivity window is configured. Showing nothing where a
 * deadline belongs reads as "this chat is permanent", which is a worse lie than
 * an approximate date — so the local clock covers the gap rather than blanking.
 */
export function deletionOf(
  channel: Channel,
  retention: RetentionConfig,
  /*
   * Only the guessed deadline below reads this, and only to notice it has been
   * missed. Defaulted rather than required because `DeletionPill` deliberately
   * does not subscribe to the tick — a list of open chats re-rendering every
   * second to draw nothing is the cost that shape exists to avoid — and a guess
   * that has expired stays expired, so sampling it per render is enough.
   */
  now: number = Date.now(),
): Deletion | null {
  if (channel.deleteAt) {
    const at = Date.parse(channel.deleteAt);
    return { reason: 'read', startedAt: at - retention.endChatWindowMs, at };
  }

  const startedAt = Date.parse(channel.lastActivityAt);

  // The backend's own deadline, when it has published one. `startedAt` still
  // comes from the last message: it is genuinely when the idle clock began, and
  // feeds the progress ring only, never the deadline.
  if (channel.inactivityDeleteAt) {
    return { reason: 'inactivity', startedAt, at: Date.parse(channel.inactivityDeleteAt) };
  }

  if (retention.inactivityDays <= 0) return null;
  const at = startedAt + retention.inactivityDays * 24 * 60 * 60 * 1000;

  /*
   * The guess, once it has been proved wrong, is withdrawn.
   *
   * This branch is a client-side prediction about a backend worker, drawn from
   * a different store than the worker reads: the deadline comes from Stream's
   * `last_activity_at` plus a constant, while the sweep enumerates
   * stream-chat-backend's own mirror and skips anything not `active` or held by
   * a marketing member. So a channel can be invisible to the sweep while we go
   * on counting it down — and because the countdown clamps at zero, it then sat
   * at "0:00" in urgent red, forever, on a chat that still sends messages.
   *
   * Being here after the moment we named is proof the prediction was wrong.
   * Saying nothing is the honest answer, and the one every caller already
   * draws for a channel with no deadline.
   *
   * Deliberately not applied to the two branches above: those are deadlines the
   * backend published rather than ones we inferred, and a sweep that runs on a
   * schedule is expected to lag its own stamp by a little.
   */
  if (at <= now) return null;

  return { reason: 'inactivity', startedAt, at };
}

/** How much of the window is left, 0–1. Drives the depleting countdown ring. */
export function deletionRemaining(deletion: Deletion, now: number): number {
  const span = deletion.at - deletion.startedAt;
  if (span <= 0) return 0;
  return Math.min(1, Math.max(0, (deletion.at - now) / span));
}

export type DeletionUrgency = 'normal' | 'warning' | 'urgent';

const HOUR = 60 * 60 * 1000;

/** How loudly to render a countdown, on absolute time left (not a fraction). */
export function deletionUrgency(deletion: Deletion, now: number): DeletionUrgency {
  const left = deletion.at - now;
  if (left < 24 * HOUR) return 'urgent';
  if (left < 3 * 24 * HOUR) return 'warning';
  return 'normal';
}

// ── job-bounded + hiring stage machine ───────────────────────────────────────
/** A conversation tied to a specific job application (vs. a plain direct chat). */
export function isJobBounded(channel: Channel): boolean {
  return channel.jobMeta != null;
}

/** Current hiring stage, treating an unset status as "pending". */
export function applicationStatusOf(channel: Channel): ApplicationStatus {
  return channel.applicationStatus ?? 'pending';
}

export const APPLICATION_STATUSES: ApplicationStatus[] = [
  'pending',
  'shortlisted',
  'interview',
  'hired',
  'rejected',
];

/** How a stage reads on screen. Upstream's "pending" is an application nobody
 *  has acted on yet, and the employer's word for that is new. */
export const APPLICATION_STATUS_LABELS: Record<ApplicationStatus, string> = {
  pending: 'New',
  shortlisted: 'Shortlisted',
  interview: 'Interviewed',
  hired: 'Hired',
  rejected: 'Rejected',
};

/** The five rungs, zeros included, for a cut that holds at least one
 *  application. Nothing for a cut that holds none (a saved list, the Direct
 *  view): a ladder of zeros there would be tickable yet could apply to nothing. */
export function applicationStatusOptions(
  facets: readonly { key: string; count: number }[],
): { key: ApplicationStatus; label: string; count: number }[] {
  if (facets.length === 0) return [];
  return APPLICATION_STATUSES.map((key) => ({
    key,
    label: APPLICATION_STATUS_LABELS[key],
    count: facets.find((f) => f.key === key)?.count ?? 0,
  }));
}

export function isApplicationStatus(value: unknown): value is ApplicationStatus {
  return typeof value === 'string' && (APPLICATION_STATUSES as string[]).includes(value);
}

/** The three employer-assignable hiring stages, in pipeline order. */
export const HIRING_STAGES = ['shortlisted', 'interview', 'hired'] as const;

export type HiringStage = (typeof HIRING_STAGES)[number];

export function isHiringStage(value: ApplicationStatus): value is HiringStage {
  return (HIRING_STAGES as readonly string[]).includes(value);
}

/**
 * Whether `to` may be assigned given the current `from`. A near-one-way machine:
 * the three stages move freely and into "rejected", a set stage never returns to
 * "pending", and "rejected" is terminal.
 */
export function canAssignApplicationStatus(
  from: ApplicationStatus,
  to: ApplicationStatus,
): boolean {
  if (to === 'pending') return false; // entry-only; a stage is never un-set
  if (from === 'rejected') return false; // terminal
  return true;
}

/**
 * WHAT ENDING THIS CHAT MEANS. A chat with an application behind it closes as a
 * rejection; one without simply closes. The two are not interchangeable
 * wordings of one act — different glyphs, different words (`ENDING_META`), and
 * only one writes a status.
 *
 * Here rather than at the call sites because it was written out as
 * `jobBounded ? 'reject' : 'close'` in several of them, and a rule spread over
 * that many expressions is one that eventually disagrees with itself.
 *
 * A hire does not change the answer. Ending a hired job chat as a rejection
 * writes `rejected` over the hire, which `canAssignApplicationStatus` permits —
 * but the button says "End & reject" while it does so, and an offer that falls
 * through after a hire is a real thing to record. The alternative was a hired
 * chat wearing the direct chat's "End chat", which reads as a chat that never
 * had an application at all.
 */
export function closingVariantFor(jobBounded: boolean): ClosingVariant {
  return jobBounded ? 'reject' : 'close';
}

/** What a narrow hiring surface draws on itself, and by omission what it files
 *  in the overflow. */
export interface HiringOffer {
  /** Stage moves drawn on the surface, in pipeline order. */
  stages: HiringStage[];
  /** Whether the ending is drawn there too, rather than in the overflow. */
  ending: boolean;
}

/**
 * WHAT A SURFACE WITH ROOM FOR ONE CONTROL PUTS ON ITSELF.
 *
 * Rejecting is the move employers actually make — the data team's reading of
 * the funnel — so this is not simply "the next stage forward", which is what it
 * was. A surface that offers Shortlist and files the rejection two taps away
 * costs one click on the uncommon path and three on the common one.
 *
 *   pending      Shortlist, and the ending. The two readings of a first pass,
 *                with nothing between them: Interview and Hire are not moves
 *                anybody makes on an applicant they have not shortlisted.
 *   a set stage  The one step forward. Somebody has already judged this
 *                applicant worth keeping, so the ending steps back to the
 *                overflow, and the step after next steps back with it — a
 *                surface offering Interview AND Hire asks the employer to pick
 *                where the pipeline already has an answer.
 *   hired        Nothing. The pipeline is spent; rejecting somebody already
 *                hired is a real thing to be able to do and a strange thing to
 *                hold out.
 *
 * WHATEVER IS NOT ON THE SURFACE IS IN THE OVERFLOW. This decides reach, not
 * permission — `canAssignApplicationStatus` is still the only thing deciding
 * what may be assigned.
 *
 * Read by the widget alone. The full page draws every stage at once and needs
 * no such choice; see `HiringStepper`.
 */
export function hiringOffer(from: ApplicationStatus): HiringOffer {
  if (from === 'pending') return { stages: [HIRING_STAGES[0]], ending: true };
  // A rejected chat is an ended chat, and an ended chat reports its outcome
  // instead of offering anything. Answered as an empty offer rather than thrown
  // over, so a surface that draws it draws nothing.
  if (!isHiringStage(from)) return { stages: [], ending: false };
  const next = HIRING_STAGES[HIRING_STAGES.indexOf(from) + 1];
  return { stages: next ? [next] : [], ending: false };
}

// ── bulk actions ─────────────────────────────────────────────────────────────
/**
 * What the inbox can do to a set of chats at once: message everyone, move them to
 * a hiring stage, or end them — which rejects the ones with a job behind them and
 * closes the rest. In the order the selection bar offers them. Ported from the
 * mock's `channelRules`.
 */
export const BULK_ACTIONS = ['message', ...HIRING_STAGES, 'end'] as const;

export type BulkAction = (typeof BULK_ACTIONS)[number];

/**
 * Whether a bulk action can land on this chat — one rule the menu counts eligible
 * chats with (greying a row at zero) and the run skips with, so what the employer
 * is told and what actually happens cannot disagree. A stage attaches to an
 * application, so a direct chat takes none of the three; ending is not a stage and
 * reaches any open chat — what differs is whether it also rejects, decided per
 * chat at run time (see the End apply). Ended chats are read-only and broadcasts
 * have nobody to answer, so neither takes any action.
 */
export function acceptsBulkAction(channel: Channel, action: BulkAction): boolean {
  if (isEnded(channel) || isBroadcast(channel)) return false;
  if (action === 'message' || action === 'end') return true;
  if (!isJobBounded(channel)) return false;
  return canAssignApplicationStatus(applicationStatusOf(channel), action);
}

/**
 * The eligible channel ids per bulk action over a picked set: the subset each
 * action reaches, and — against the selection size — the count it skips. One pass,
 * so the bar's greying/counts and the dialogs' targets read one derivation.
 */
export function bulkTargets(channels: Channel[]): Record<BulkAction, ChannelId[]> {
  const targets: Record<BulkAction, ChannelId[]> = {
    message: [],
    shortlisted: [],
    interview: [],
    hired: [],
    end: [],
  };
  for (const channel of channels) {
    for (const action of BULK_ACTIONS) {
      if (acceptsBulkAction(channel, action)) targets[action].push(channel.id);
    }
  }
  return targets;
}

// ── broadcast + sorting + counts + search ────────────────────────────────────

/**
 * Jobie, the platform's announcer — the one every important message is planned
 * to come from.
 *
 * The exact id, not a `jobie_` prefix. The `_ajt` here is not this thread's
 * brand the way it is in `isBroadcastChannel`: one persona carries the
 * announcements for the estate, so a `jobie_mk` or `jobie_rb` appearing later
 * would be somebody else, and opening their thread as if it were Jobie's is the
 * mistake a prefix match would make silently.
 */
const JOBIE_ID = 'jobie_ajt';

/**
 * Whether a participant is Jobie.
 *
 * A different question from `isBroadcast`, which asks whether a thread is an
 * announcement at all: the Info rail is a section, and other personas may post
 * there. This names one of them, because the post-login landing has to be
 * Jobie's thread and not merely the newest thing marketing sent.
 *
 * Read off the participant we already hold rather than rebuilt from the channel
 * id (`chat-mkt-jobie_ajt-emp-<employerId>`). That id carries the employer's own
 * row id, which is what put it in a URL in the first place; this asks the loaded
 * list who it is talking to instead.
 */
export function isJobie(participantId: string): boolean {
  return participantId === JOBIE_ID;
}

/**
 * A platform announcement thread rather than a conversation with a person. One
 * concept, three vocabularies: the rail calls it "Info", the read model stores
 * `kind = 'info'`, the channel's own origin is `marketing_campaign`.
 *
 * The source is authoritative; the member-id suffix stays only for channels the
 * AI-2336 backfill did not stamp, which `isInfoSource` cannot tell from a chat.
 *
 * Takes the fields it reads rather than a whole `Channel`, so `mapChannel` can
 * ask while still assembling one and the two cannot disagree.
 *
 * The verdict itself lives in `shared`, because the widget's unread badge asks
 * the same question server-side and the two must not drift.
 */
export function isBroadcast(channel: Pick<Channel, 'memberIds' | 'source'>): boolean {
  return isBroadcastChannel(channel.source, channel.memberIds);
}

export function sortByRecentActivity(channels: Channel[]): Channel[] {
  return [...channels].sort((a, b) => Date.parse(b.lastActivityAt) - Date.parse(a.lastActivityAt));
}

export function totalUnread(channels: Channel[]): number {
  return channels.reduce((sum, channel) => sum + channel.unreadCount, 0);
}

/** How many chats are waiting on a reply — the widget badge. Inbox category only. */
export function pendingReplyCount(channels: Channel[]): number {
  return channels.filter((channel) => channelCategory(channel) === 'inbox' && needsReply(channel))
    .length;
}

export function matchesSearch(channel: Channel, counterpartName: string, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle === '') return true;
  return (
    counterpartName.toLowerCase().includes(needle) ||
    channel.lastMessageText.toLowerCase().includes(needle)
  );
}

// ── participant ──────────────────────────────────────────────────────────────
/**
 * The company an employer speaks for, read off the last `·`-separated part of
 * their headline. Falls back to the person's own name.
 */
export function companyOf(participant: Participant): string {
  return participant.headline?.split('·').pop()?.trim() || participant.name;
}

// ── gamification ─────────────────────────────────────────────────────────────
/** Per-channel verdict for the "Reply to get +N coins" nudge. */
export function firstReplyReward(
  channel: Channel,
  config: GamificationConfig | null,
): FirstReplyReward {
  const coins = config?.coins.first_reply ?? 0;
  const limit = config?.coins.limit ?? 0;
  const eligible = channel.jobMeta != null && limit > 0 && coins > 0 && channel.firstReplyPending;
  return { eligible, coins };
}

// ── inbox filter / group ─────────────────────────────────────────────────────
export function filterInbox(items: InboxItem[], criteria: InboxCriteria): InboxItem[] {
  const { query, scope, jobIds } = criteria;
  return items.filter(
    (item) =>
      (scope === 'all' || item.needsReply) &&
      (jobIds.length === 0 || jobIds.includes(item.channel.jobMeta?.jobId ?? '')) &&
      matchesSearch(item.channel, item.counterpart.name, query),
  );
}

export function groupInbox(items: InboxItem[], groupBy: GroupBy): InboxGroup[] {
  // 'stage' is a server-side grouping only (it needs facet counts over the whole
  // inbox, not the page in hand), so it renders ungrouped here rather than
  // silently falling through to person grouping.
  if (groupBy === 'none' || groupBy === 'stage') return [{ key: 'all', label: '', items }];

  const groups = new Map<string, InboxGroup>();
  for (const item of items) {
    const [key, label] =
      groupBy === 'job'
        ? [
            item.channel.jobMeta?.jobId ?? '__direct',
            item.channel.jobMeta?.jobTitle ?? 'Direct messages',
          ]
        : [item.counterpart.id, item.counterpart.name];
    const existing = groups.get(key);
    if (existing) existing.items.push(item);
    else groups.set(key, { key, label, items: [item] });
  }

  return [...groups.values()].sort(
    (a, b) =>
      Date.parse(b.items[0]!.channel.lastActivityAt) -
      Date.parse(a.items[0]!.channel.lastActivityAt),
  );
}
